import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, X, ArrowRight, Minimize2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { formatPoints } from '../utils/levelCalculation';
import type { Habit } from '../types';

interface HabitTimerSessionProps {
  habit: Habit;
  onComplete: (durationSeconds: number, xpEarned: number) => void;
  onMinimize: () => void;
  onCancel: () => void;
}

export function HabitTimerSession({ habit, onComplete, onMinimize, onCancel }: HabitTimerSessionProps) {
  const [timerDuration, setTimerDuration] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  const { activeSession, calculateSessionXP, updateElapsedTime } = useHabitSessionStore();
  const { getCategoryColor, getCategoryName } = useCategories();
  const timerRef = useRef<number>();

  const categoryColor = getCategoryColor(habit.category);
  const HabitIcon = habit.icon && (Icons[habit.icon as keyof typeof Icons] as any)
    ? (Icons[habit.icon as keyof typeof Icons] as any)
    : Icons.Star;

  const elapsedSeconds = activeSession?.elapsedSeconds || 0;
  const currentXP = calculateSessionXP(elapsedSeconds);

  useEffect(() => {
    if (timerDuration !== null) {
      setTimeRemaining(timerDuration * 60);
    }
  }, [timerDuration]);

  useEffect(() => {
    if (isPaused || timeRemaining <= 0 || timerDuration === null) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      return;
    }

    timerRef.current = window.setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        updateElapsedTime();

        if (newTime <= 0) {
          clearInterval(timerRef.current);
          handleComplete();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPaused, timeRemaining, timerDuration]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (!timerDuration) return 0;
    const totalSeconds = timerDuration * 60;
    return ((totalSeconds - timeRemaining) / totalSeconds) * 100;
  };

  const handleComplete = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const finalXP = calculateSessionXP(elapsedSeconds);
    onComplete(elapsedSeconds, finalXP);
  };

  const handleStop = () => {
    setShowConfirmExit(true);
  };

  const confirmStop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const finalXP = calculateSessionXP(elapsedSeconds);
    onComplete(elapsedSeconds, finalXP);
  };

  if (timerDuration === null) {
    return (
      <div className="fixed inset-0 bg-background z-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${categoryColor}15` }}
                >
                  <HabitIcon className="w-6 h-6" style={{ color: categoryColor }} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    {habit.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {getCategoryName(habit.category)}
                  </p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="p-2 hover:bg-muted rounded-md transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Set timer duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="240"
                placeholder="30"
                className="w-full px-4 py-3 bg-background border border-input rounded-lg text-lg text-center"
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value > 0 && value <= 240) {
                    setTimerDuration(value);
                  }
                }}
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Timer will countdown from your set duration
              </p>
            </div>

            <button
              onClick={() => {
                if (timerDuration && timerDuration > 0) {
                  setIsPaused(false);
                }
              }}
              disabled={!timerDuration || timerDuration <= 0}
              className="w-full py-3 rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: categoryColor
              }}
            >
              Start Timer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div
            className="bg-card rounded-lg border border-border p-8"
            style={{ backgroundColor: `${categoryColor}05` }}
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <HabitIcon className="w-8 h-8" style={{ color: categoryColor }} />
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    {habit.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {getCategoryName(habit.category)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onMinimize}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  title="Minimize"
                >
                  <Minimize2 className="w-5 h-5 text-muted-foreground" />
                </button>
                <button
                  onClick={handleStop}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                  title="Stop Session"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="text-7xl font-bold mb-4" style={{ color: categoryColor }}>
                {formatTime(timeRemaining)}
              </div>
              <p className="text-sm text-muted-foreground">
                Time Remaining
              </p>
            </div>

            <div className="relative h-3 bg-muted rounded-full overflow-hidden mb-8">
              <div
                className="absolute inset-y-0 left-0 transition-all duration-1000"
                style={{
                  width: `${getProgress()}%`,
                  backgroundColor: categoryColor
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-1">Elapsed Time</p>
                <p className="text-2xl font-semibold text-foreground">
                  {formatTime(elapsedSeconds)}
                </p>
              </div>
              <div className="p-4 bg-background rounded-lg border border-border">
                <p className="text-sm text-muted-foreground mb-1">Current XP</p>
                <p
                  className="text-2xl font-semibold"
                  style={{ color: categoryColor }}
                >
                  {formatPoints(currentXP)}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setIsPaused(!isPaused)}
                className="p-6 rounded-full hover:bg-muted transition-colors"
                style={{ color: categoryColor }}
              >
                {isPaused ? (
                  <Play className="w-10 h-10" fill={categoryColor} />
                ) : (
                  <Pause className="w-10 h-10" />
                )}
              </button>
              <button
                onClick={handleComplete}
                className="px-6 py-3 rounded-full font-semibold text-white transition-colors"
                style={{ backgroundColor: categoryColor }}
              >
                Complete Session
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConfirmExit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Stop Session?
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Your progress will be saved and you'll earn XP based on the time completed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmExit(false)}
                className="flex-1 px-4 py-2 border border-input rounded-md hover:bg-muted transition-colors"
              >
                Continue
              </button>
              <button
                onClick={confirmStop}
                className="flex-1 px-4 py-2 rounded-md text-white transition-colors"
                style={{ backgroundColor: categoryColor }}
              >
                Stop & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
